import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";

const links = [
  { label: "Collections", id: "collections", type: "section" },
  { label: "Catalogue", id: "shop", type: "route" },
  { label: "Lookbook", id: "lookbook", type: "section" },
  { label: "Manifesto", id: "manifesto", type: "section" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { count, setOpen } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (l) => {
    setMobileOpen(false);
    if (l.type === "route") {
      navigate("/shop");
      return;
    }
    if (location.pathname !== "/") {
      navigate(`/#${l.id}`);
    } else {
      document.getElementById(l.id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <motion.header
      data-testid="main-nav"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex items-center justify-between px-6 md:px-12 h-20">
        <button
          data-testid="nav-logo"
          onClick={() => {
            if (location.pathname !== "/") navigate("/");
            else window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="font-display text-xl font-extrabold tracking-tighter text-white"
        >
          GHOSTLINE
          <span className="text-steel-light">.</span>
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => handleNav(l)}
              className="font-mono-tech text-xs uppercase tracking-widest text-steel-light hover:text-white transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-5">
          <button
            data-testid="open-cart-btn"
            onClick={() => setOpen(true)}
            className="relative text-white group"
            aria-label="Open cart"
          >
            <ShoppingBag className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-2 -right-2 bg-white text-black text-[10px] font-mono-tech w-4 h-4 flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden text-white"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black/95 border-t border-white/10"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-link-${l.id}`}
                  onClick={() => handleNav(l)}
                  className="font-display text-2xl font-bold tracking-tight text-white text-left"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
